import type { FC } from 'react';
import { useMemo } from 'react';
import { Box, CircularProgress, Stack } from '@mui/material';

import GuidedTourIcon from 'src/components/app/context/GuidedTourContext/components/guidedTourIcon';
import Accordion from 'src/components/common/Accordion';
import type { OrgTreeState } from 'src/components/common/OrgTree/types';
import { useDashboardContext } from 'src/components/dashboard/DashboardContext';
import OrgTreeFilter from 'src/components/dashboard/DashboardPage/components/DashboardSidebar/accordionItems/OrgTreeFilter';
import TicketFilter from 'src/components/dashboard/DashboardPage/components/DashboardSidebar/accordionItems/TicketFilter';
import VariableTypeAccordionItem from 'src/components/dashboard/DashboardPage/components/VariableTypeAccordionItem';
import useComparisonMode from 'src/components/dashboard/hooks/useComparisonMode';
import ComparisonFilter, {
  COMPARISON_FILTER_NAME,
} from 'src/components/followUp/FilterColumn/ComparisonFilter/ComparisonFilter';
import DateFilter from 'src/components/followUp/FilterColumn/DateFilter/DateFilter';

import { activeItemsReducer, TICKET_FILTER_NAME } from './helper';

import { styles } from './index.styles';

export interface DashboardSidebarProps {
  orgTreeState: OrgTreeState | null;
  guidedTourId: string;
}

const DashboardSidebar: FC<DashboardSidebarProps> = ({
  orgTreeState,
  guidedTourId,
}) => {
  const { dashboardConfig, loading, filters } = useDashboardContext();
  const comparison = useComparisonMode();

  const {
    rangeFilter,
    ticketFilterSearch,
    ticketFilterNps,
    ticketFilterStatus,
    ticketFilterOwner,
    ticketFilterSurvey,
  } = filters;

  const ticketFilterActive = !!(
    ticketFilterSearch ||
    ticketFilterNps ||
    ticketFilterStatus ||
    ticketFilterOwner
  );

  const {
    variable_types: variableTypes = [],
    guided_tour_org_tree_step: orgTreeStep,
    guided_tour_second_org_tree_step: orgTreeSecondStep,
    disable_org_unit_selection_based_on_status,
  } = dashboardConfig || {};
  const hasGuidedTour = !!orgTreeStep || !!orgTreeSecondStep;

  // The comparison against the comparison survey is not folded away: its toggle stays in view.
  const initiallyActiveItems = useMemo(
    () => [
      ...activeItemsReducer(filters),
      ...(comparison === 'comparisonSurvey' ? [COMPARISON_FILTER_NAME] : []),
    ],
    [filters, comparison],
  );

  if (loading || !dashboardConfig) {
    return (
      <Stack
        direction="column"
        sx={{
          alignItems: 'center',
          py: 5,
        }}
      >
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <>
      {rangeFilter && <DateFilter />}
      {hasGuidedTour && (
        <Box sx={styles.guidedTour}>
          <GuidedTourIcon iconId={guidedTourId} />
        </Box>
      )}

      <Accordion initiallyActiveItems={initiallyActiveItems}>
        {orgTreeState && (
          <OrgTreeFilter
            itemName="orgUnitFilter"
            orgTreeState={orgTreeState}
            disableOrgUnitSelectionBasedOnStatus={
              disable_org_unit_selection_based_on_status
            }
          />
        )}

        {ticketFilterActive && (
          <TicketFilter
            itemName={TICKET_FILTER_NAME}
            filterColumnSections={{
              isGlobalDashboard: dashboardConfig.is_global_dashboard,
              search: !!ticketFilterSearch,
              nps: !!ticketFilterNps,
              status: !!ticketFilterStatus,
              owner: !!ticketFilterOwner,
              survey: !!ticketFilterSurvey,
            }}
          />
        )}

        {variableTypes.map((variableType) => (
          <VariableTypeAccordionItem
            key={variableType.id}
            variableType={variableType}
          />
        ))}

        {comparison && <ComparisonFilter range />}
      </Accordion>
    </>
  );
};

export default DashboardSidebar;
